<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewBudgets' }" class="mb-0 main-identifier fw-semibold text-decoration-none"
        title="Go back to Budgets">
        Budgets
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Budget Year</p>
    </div>

    <div class="d-flex justify-content-center mb-5">

      <div class="form-outer-wrapper">
        <div class="form-outer" style="max-width: 1500px;">
          <form>
            <div class="crud-form position-relative">

              <div class="form-inner mb-2">
                <div class="form-header d-flex justify-content-between align-items-center">
                  <h1 class="mb-0">Budget Year</h1>
                </div>

                <div class="budget-area position-relative px-4">
                  <div v-if="loadingDetails" class="loading-state text-center py-5">
                    <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                    <p class="mt-2 mb-0 text-muted">Loading budget year...</p>
                  </div>

                  <div v-if="error && !loadingDetails" class="alert alert-warning mt-3" role="alert">
                    {{ error }}
                  </div>

                  <div class="budget-title mb-4 mt-4">
                    <div class="row mb-3">
                      <!-- Budget Year -->
                      <div class="col-md-12 row form-group position-relative floating-label-group">
                        <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                          <label for="year" class="form-label">Budget Year</label>
                        </div>
                        <div class="col-md-9 input-right">
                          <input id="budgetYear" type="text" class="form-control form-view-only-input"
                            :value="form.year || 'N/A'" placeholder="" readonly />
                        </div>
                      </div>
                      <!-- Amount Budgeted -->
                      <div class="col-md-12 row form-group position-relative floating-label-group">
                        <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                          <label for="totalAmountBudgeted" class="form-label">Total Amount Budgeted</label>
                        </div>
                        <div class="col-md-9 input-right">
                          <input id="totalAmountBudgeted" type="text" class="form-control form-view-only-input"
                            :value="formatCurrency(form.amountBudgeted || 0)" placeholder="" readonly />
                        </div>
                      </div>
                      <!-- Amount Released -->
                      <div class="col-md-12 row form-group position-relative floating-label-group">
                        <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                          <label for="totalAmountReleased" class="form-label">Total Amount Released</label>
                        </div>
                        <div class="col-md-9 input-right">
                          <input id="totalAmountReleased" type="text" class="form-control form-view-only-input"
                            :value="formatCurrency(form.amountReleased || 0)" placeholder="" readonly />
                        </div>
                      </div>
                      <!-- Balance -->
                      <div class="col-md-12 row form-group position-relative floating-label-group">
                        <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                          <label for="balance" class="form-label">Balance</label>
                        </div>
                        <div class="col-md-9 input-right">
                          <input id="balance" type="text" class="form-control form-view-only-input"
                            :value="formatCurrency(form.balance || 0)" placeholder="" readonly />
                        </div>
                      </div>
                      <!-- Performance -->
                      <div class="col-md-12 row form-group position-relative floating-label-group">
                        <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                          <label for="performance" class="form-label">Performance</label>
                        </div>
                        <div class="col-md-9 input-right">
                          <input id="performance" type="text" class="form-control form-view-only-input"
                            :value="Number(form.performance).toFixed(2) + '%' || 'N/A'" placeholder="" readonly />
                        </div>
                      </div>
                      <!-- Revenue Amount -->
                      <div class="col-md-12 row form-group position-relative floating-label-group">
                        <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                          <label for="totalRevenueAmount" class="form-label">Total Revenue Amount</label>
                        </div>
                        <div class="col-md-9 input-right">
                          <input id="totalRevenueAmount" type="text" class="form-control form-view-only-input"
                            :value="formatCurrency(form.revenueAmount || 0)" placeholder="" readonly />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Table Controls -->
                  <div class="table-controls mt-3">
                    <div class="table-controls-top">
                      <div class="table-controls-left">
                        <div class="entries-control">
                          <span>Show</span>
                          <select class="form-select form-select-sm" v-model.number="pageLength">
                            <option :value="10">10</option>
                            <option :value="25">25</option>
                            <option :value="50">50</option>
                            <option :value="100">100</option>
                          </select>
                          <span>Entries</span>
                        </div>

                        <div class="search-box" style="max-width: 100%;">
                          <span class="material-symbols-outlined search-icon">search</span>
                          <input type="search" v-model="search" class="form-control form-control-sm search-input"
                            placeholder="Search by mda, economic line item, amount budgeted or revenue amount..." />
                        </div>

                        <div class="export-links">
                          <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to
                            PDF</a>
                          <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export
                            to Excel</a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Table -->
                  <div class="table-wrapper">
                    <div class="table-container">
                      <table class="responsive-table">
                        <thead>
                          <tr>
                            <th></th>
                            <th class="sortable-header" style="min-width: 150px;">
                              <div class="th-content">
                                <span>MDA</span>
                                <div class="sort-icons">
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'mdaName' && sortOrder === 'asc' }"
                                    @click="setSort('mdaName', 'asc')">
                                    arrow_drop_up
                                  </span>
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'mdaName' && sortOrder === 'desc' }"
                                    @click="setSort('mdaName', 'desc')">
                                    arrow_drop_down
                                  </span>
                                </div>
                              </div>
                            </th>
                            <th class="sortable-header">
                              <div class="th-content">
                                <span>Economic Line Item</span>
                                <div class="sort-icons">
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'econLineItemName' && sortOrder === 'asc' }"
                                    @click="setSort('econLineItemName', 'asc')">
                                    arrow_drop_up
                                  </span>
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'econLineItemName' && sortOrder === 'desc' }"
                                    @click="setSort('econLineItemName', 'desc')">
                                    arrow_drop_down
                                  </span>
                                </div>
                              </div>
                            </th>
                            <th class="sortable-header">
                              <div class="th-content">
                                <span>Amount Budgeted</span>
                                <div class="sort-icons">
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'amountBudgeted' && sortOrder === 'asc' }"
                                    @click="setSort('amountBudgeted', 'asc')">
                                    arrow_drop_up
                                  </span>
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'amountBudgeted' && sortOrder === 'desc' }"
                                    @click="setSort('amountBudgeted', 'desc')">
                                    arrow_drop_down
                                  </span>
                                </div>
                              </div>
                            </th>
                            <th class="sortable-header">
                              <div class="th-content">
                                <span>Amount Released</span>
                                <div class="sort-icons">
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'amountReleased' && sortOrder === 'asc' }"
                                    @click="setSort('amountReleased', 'asc')">
                                    arrow_drop_up
                                  </span>
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'amountReleased' && sortOrder === 'desc' }"
                                    @click="setSort('amountReleased', 'desc')">
                                    arrow_drop_down
                                  </span>
                                </div>
                              </div>
                            </th>
                            <th class="sortable-header">
                              <div class="th-content">
                                <span>Balance</span>
                                <div class="sort-icons">
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'balance' && sortOrder === 'asc' }"
                                    @click="setSort('balance', 'asc')">
                                    arrow_drop_up
                                  </span>
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'balance' && sortOrder === 'desc' }"
                                    @click="setSort('balance', 'desc')">
                                    arrow_drop_down
                                  </span>
                                </div>
                              </div>
                            </th>
                            <th class="sortable-header">
                              <div class="th-content">
                                <span>Performance</span>
                                <div class="sort-icons">
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'performance' && sortOrder === 'asc' }"
                                    @click="setSort('performance', 'asc')">
                                    arrow_drop_up
                                  </span>
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'performance' && sortOrder === 'desc' }"
                                    @click="setSort('performance', 'desc')">
                                    arrow_drop_down
                                  </span>
                                </div>
                              </div>
                            </th>
                            <th class="sortable-header">
                              <div class="th-content">
                                <span>Revenue Amount</span>
                                <div class="sort-icons">
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'revenueAmount' && sortOrder === 'asc' }"
                                    @click="setSort('revenueAmount', 'asc')">
                                    arrow_drop_up
                                  </span>
                                  <span class="material-symbols-outlined sort-icon"
                                    :class="{ active: sortKey === 'revenueAmount' && sortOrder === 'desc' }"
                                    @click="setSort('revenueAmount', 'desc')">
                                    arrow_drop_down
                                  </span>
                                </div>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(budget, index) in paginatedItems" :key="budget.id"
                            :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                            <td data-label="S/N" class="serial-no text-center">
                              {{ (currentPage - 1) * pageLength + index + 1 }}
                            </td>
                            <td data-label="MDA" class="primary-col fw-semibold">
                              {{ budget.mdaName }}</td>
                            <td data-label="Economic Line Item" class="secondary-col">
                              <router-link
                                :to="{ name: 'ViewEliBudget', params: { id: budget.id } }"
                                class="eli-link">
                                {{ budget.econLineItemName }}
                              </router-link>
                            </td>
                            <td data-label="Amount Budgeted" class="fw-semibold text-end">
                              {{ formatCurrency(budget.amountBudgeted || 0) }}
                            </td>
                            <td data-label="Amount Released" class="fw-semibold text-end">
                              {{ formatCurrency(budget.amountReleased || 0) }}
                            </td>
                            <td data-label="Amount Budgeted" class="fw-semibold text-end">
                              {{ formatCurrency(budget.balance || 0) }}
                            </td>
                            <td data-label="Performance" class="fw-semibold text-end">
                              {{Number(budget.performance).toFixed(2) || 0 }}%
                            </td>
                            <td data-label="Revenue Amount" class="fw-semibold text-end">
                              {{ formatCurrency(budget.revenueAmount || 0) }}
                            </td>
                          </tr>

                          <tr v-if="budget.length === 0">
                            <td :colspan="5" class="text-center text-muted py-4">
                              <div>No budget to display.</div>
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <!-- Pagination Summary and Actions -->
                      <div v-if="!loadingDetails && budget.length"
                        class="pagination-info d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 ms-1 mb-1">
                        <!-- Summary -->
                        <small class="text-muted">
                          Showing
                          <strong>{{ (currentPage - 1) * pageLength + 1 }}</strong>
                          to
                          <strong>{{ Math.min(currentPage * pageLength, filteredItems.length) }}</strong>
                          of
                          <strong>{{ filteredItems.length.toLocaleString() }}</strong>
                          entries
                        </small>

                        <!-- Pagination Controls -->
                        <nav class="pagination-nav me-1" aria-label="Page navigation">
                          <ul class="pagination pagination-sm mb-0">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                              <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Previous</a>
                            </li>
                            <li v-for="(page, index) in visiblePages" :key="index" class="page-item"
                              :class="{ active: currentPage === page, disabled: page === '...' }">
                              <a v-if="page !== '...'" class="page-link" href="#" @click.prevent="changePage(page)">
                                {{ page }}
                              </a>

                              <span v-else class="page-link">...</span>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                              <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</a>
                            </li>
                          </ul>
                        </nav>

                      </div>

                    </div>
                  </div>

                </div>
              </div>
            </div>

          </form>

        </div>

      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import axios from 'axios'
import DashboardLayout from '../layout/DashboardLayout.vue'
import Cookies from 'js-cookie';
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import { debounce } from 'lodash'
import { useRoute } from 'vue-router';
import { useUserStore } from '@/stores/user';
const route = useRoute()
const userStore = useUserStore()
const year = route.params.year || route.query.year

const root_api_url = process.env.VUE_APP_API_ROOT_URL

const error = ref('')
const sortKey = ref('')
const sortOrder = ref('asc')
const setSort = (key, order) => {
  sortKey.value = key
  sortOrder.value = order
  currentPage.value = 1
}
const search = ref('');
const budget = ref([]);
const pageLength = ref(25);
const currentPage = ref(1);
const loadingDetails = ref(false);

const form = reactive({
  year: '',
  amountBudgeted: 0,
  amountReleased: 0,
  revenueAmount: 0,
  balance: 0,
  performance: 0,
  itemBudgetDtoList: [],
  errorMessage: '',
})

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

const filteredItems = computed(() => {
  let items = budget.value

  if (debouncedSearch.value) {
    const term = debouncedSearch.value.toLowerCase()
    items = items.filter(item =>
      item.mdaName.toLowerCase().includes(term) ||
      item.econLineItemName.toLowerCase().includes(term) ||
      String(item.amountBudgeted).toLowerCase().includes(term) ||
      String(item.revenueAmount).toLowerCase().includes(term)
    )
  }

  if (sortKey.value) {
    items = [...items].sort((a, b) => {
      const aVal = a[sortKey.value]
      const bVal = b[sortKey.value]
      const aNum = Number(aVal)
      const bNum = Number(bVal)
      const isNumeric = !isNaN(aNum) && !isNaN(bNum)

      let result = isNumeric
        ? aNum - bNum
        : String(aVal).localeCompare(String(bVal))

      return sortOrder.value === 'asc' ? result : -result
    })
  }

  return items
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const visiblePages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  const delta = 2 // pages around current

  const range = []
  const rangeWithDots = []

  let l

  for (let i = 1; i <= total; i++) {
    if (
      i === 1 ||
      i === total ||
      (i >= current - delta && i <= current + delta)
    ) {
      range.push(i)
    }
  }

  for (let i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1)
      } else if (i - l > 2) {
        rangeWithDots.push('...')
      }
    }
    rangeWithDots.push(i)
    l = i
  }

  return rangeWithDots
})

const fetchBudgetYear = async () => {
  if (!year) {
    return
  }
  error.value = ''
  loadingDetails.value = true
  const token = Cookies.get('auth_token');
  try {
    const { data } = await axios.get(`${root_api_url}/eli/mda-eli-budget/budget-details/${year}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    form.year = data.year || ''
    form.amountBudgeted = data.amountBudgeted || 0
    form.amountReleased = data.amountReleased || 0
    form.revenueAmount = data.revenueAmount || 0
    form.balance = data.balance || 0
    form.performance = data.performance || 0

    budget.value = (data.itemBudgetDtoList || []).map(item => ({
      id: item.id,
      mdaName: item.mdaName,
      econLineItemName: item.econLineItemName,
      economicCode: item.economicCode,
      amountBudgeted: item.amountBudgeted,
      amountReleased: item.amountReleased,
      balance: item.balance,
      performance: item.performance,
      year: item.year,
      mdaId: item.mdaId,
      economicLineItemId: item.economicLineItemId,
      revenueAmount: item.revenueAmount,
      totalRecAmtBudgeted: item.totalRecAmtBudgeted,
      totalCapAmtBudgeted: item.totalCapAmtBudgeted,
      totalRecAmtReleased: item.totalRecAmtReleased,
      totalCapAmtReleased: item.totalCapAmtReleased,
      orgCode: item.orgCode,
      errorMessage: item.errorMessage,
    }))
    form.itemBudgetDtoList = budget.value
  } catch (err) {
    console.error('Failed to fetch Budget:', err)
    error.value = 'Failed to fetch budget. Please try again.'
  } finally {
    loadingDetails.value = false
  }
}

const exportToPDF = () => {
  const doc = new jsPDF()
  autoTable(doc, {
    head: [['S/N', 'MDA', 'Economic Line Item', 'Amount Budgeted', 'Amount Released', 'Balance', 'Performance',  'Revenue Amount']],
    body: budget.value.map((budget, index) => [
      `${index + 1}`,
      budget.mdaName,
      budget.econLineItemName,
      budget.amountBudgeted,
      budget.amountReleased,
      budget.balance,
      budget.performance,
      budget.revenueAmount
    ])
  })
  doc.save('budget.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('budget')
  worksheet.addRow(['S/N', 'MDA', 'Economic Line Item', 'Amount Budgeted', 'Amount Released', 'Balance', 'Performance','Revenue Amount'])

  budget.value.forEach((budget, index) => {
    const row = worksheet.addRow([
      `${index + 1}`,
      budget.mdaName,
      budget.econLineItemName,
      budget.amountBudgeted,
      budget.amountReleased,
      budget.balance,
      budget.performance,
      budget.revenueAmount
    ])

    const currencyCols = [4, 5, 6, 8]
    currencyCols.forEach(col => {
      worksheet.getColumn(col).width = 18
      const cell = row.getCell(col)
      cell.value = Number(cell.value || 0)
      cell.numFmt = '#,##0.00'
      cell.alignment = { horizontal: 'right' }
    })
  })

  worksheet.getRow(1).font = { bold: true }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'budget.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}


const formatCurrency = (amount) => {
  return '₦' + Number(amount || 0).toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

onMounted(async () => {
  if (!userStore.profile) {
    await userStore.fetchProfile()
  }

  await fetchBudgetYear(),
    document.title = "View Budget Year"
})

</script>
<style scoped>
.view-only-input {
  background-color: transparent;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
  padding-right: 0;
  margin-right: 1rem;
  margin-bottom: 0.5rem !important;
}

.budget-area {
  min-height: 0px;
}

.loading-state {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

/* Fix horizontal scroll - override any parent overflow restrictions */
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

.budget-title {
  font-weight: 600;
  font-size: 1.25rem !important;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
  background-color:var(--table-bg-head);
}

.responsive-table {
  width: 100%;
}

.responsive-table td,
.responsive-table th {
  vertical-align: middle;
}

/* MDA column */
.primary-col {
  max-width: 400px !important;
  white-space: normal;
  word-break: break-word;
}

/* Economic Line Item column */
.secondary-col {
  max-width: 350px !important;
  white-space: normal;
  word-break: break-word;
}

/* Amount columns (keep clean and tight) */
.text-end {
  white-space: nowrap;
  min-width: 120px;
}


.sortable-header .th-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sort-icons {
  display: flex;
  flex-direction: column;
  margin-left: auto;
}

.sort-icon {
  cursor: pointer;
  font-size: 18px;
  line-height: 0.8;
  user-select: none;
}

.sort-icon.active {
  color: #0d6efd;
}

.eli-link {
  color: #0d6efd;
  font-weight: 500;
  text-decoration: none;
}

.eli-link:hover {
  text-decoration: underline;
  color: #0a58ca;
}

/* Responsive adjustments */
@media (max-width: 768px) {

  .table-controls-top {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
